import React, { useEffect, useState, useRef, Suspense } from "react";
import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import * as actions from "../actions";
import AppHoc from "../../../generics/AppHOC";
import { useI18n } from "../../../i18n/useI18n";
import Spinner from "../../../common/Spinner";
import {
  verifyDomainIdentifiers,
  validateIdpFields,
  formatDomainIdentifiers,
  compareObjects
} from "../../../utils/query-utils";
import { IDP_ACTIONS } from "../../../utils/enums";
const ErrorComponent = React.lazy(() =>
  import("../../../common/ErrorComponent")
);
import css from "../accountsMagneticStyle.less";
const [
  HbrInput,
  HbrButton,
  HbrSelect,
  HbrOption,
  HbrHighlight,
  HbrIllustration,
] = reactWrapper([
  "hbr-input",
  "hbr-button",
  "hbr-select",
  "hbr-option",
  "hbr-highlight",
  "hbr-illustration",
]);
const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(actions, dispatch)
});

const mapStateToProps = state => {
  return {
    accounts: state.vanalytics.accounts
  };
};

const Oidc = props => {
  const { tViews } = useI18n('views');
  const { actions, accounts } = props;
  const { setHideModal, defineIdpOidc, displaySpinner } = actions;
  const { idpData, idpAction, idpServerData, error, showSpinner } = accounts;
  const [state, setState] = useState({
    client_id: "",
    client_secret: "",
    issuer: "",
    auth_endpoint: "",
    token_endpoint: "",
    jwks_endpoint: "",
    userinfo_endpoint: "",
    default_user_role: "v1;*:basic,o365",
    domain_identifier: ""
  });

  const [domainIdentifier, setDomainIdentifier] = useState([]);
  const [domainOptions, setDomainOptions] = useState([]);
  const [domainSearchQuery, setDomainSearchQuery] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [stateChanged, setStateChanged] = useState(false);
  const stateRef = useRef({ ...idpServerData, client_secret: "" });

  // Check if state changed
  useEffect(() => {
    if (idpAction === IDP_ACTIONS.EDIT) {
      const stateResult = compareObjects(state, stateRef.current);
      if (!stateResult) {
        setStateChanged(true);
      } else {
        setStateChanged(false);
      }
    }
  }, [state]);

  useEffect(() => {
    if (idpAction !== IDP_ACTIONS.DEFINE) {
      if (
        idpServerData.domain_identifier &&
        idpServerData.domain_identifier.length > 0
      ) {
        const domain_identifier = formatDomainIdentifiers(
          idpServerData.domain_identifier
        );
        setDomainIdentifier(domain_identifier);
        setDomainOptions(domain_identifier);
      }
      const stateRepl = { ...state, ...idpServerData, client_secret: "" };
      setState(stateRepl);
    }
  }, [idpServerData]);

  const validateFormFields = () => {
    const formStr = {
      client_id: state.client_id,
      issuer: state.issuer,
      auth_endpoint: state.auth_endpoint,
      token_endpoint: state.token_endpoint,
      jwks_endpoint: state.jwks_endpoint,
      userinfo_endpoint: state.userinfo_endpoint,
      default_user_role: state.default_user_role,
      domain_identifier: state.domain_identifier
    };

    // Client secret is not mandatory in edit idp usecase
    if (idpAction !== IDP_ACTIONS.EDIT) {
      formStr.client_secret = state.client_secret;
    }

    const formValidations = validateIdpFields(formStr);

    // Additional validation for domain_identifier - ensure at least one domain is selected
    const currentDomains = getCurrentDomainValues();
    if (!currentDomains || currentDomains.length === 0) {
      formValidations.domain_identifier = true;
    }

    if (Object.keys(formValidations).length > 0) {
      setFormErrors(formValidations);
      return false;
    }

    // Clear any previous form errors
    setFormErrors({});

    //Case for already added invalid domains
    const isError = verifyDomainIdentifiers(state.domain_identifier);
    if (isError) {
      setValidationErrors({
        domain_identifier: true
      });
      return false;
    } else {
      setValidationErrors({});
      return true;
    }
  };

  const onSave = event => {
    event.preventDefault();
    const isFormValid = validateFormFields();
    if (isFormValid) {
      const requestPayload = { ...state };
      requestPayload["idp_type"] = "OIDC";
      requestPayload["overlay_id"] = idpData.overlay_id;
      requestPayload["va_id"] = idpData.va_id;

      if (idpData.smartAccountReference) {
        requestPayload["sa_domain"] = idpData.smartAccountReference.domain_id;
      } else {
        requestPayload["sa_domain"] = idpData.domain_id;
      }

      if (idpAction !== IDP_ACTIONS.DEFINE) {
        requestPayload["idp_id"] = idpData.idp_id;
      }

      defineIdpOidc(requestPayload);
      displaySpinner({
        showSpinner: true
      });
    }
  };

  const onClose = () => {
    setHideModal();
  };

  const handleDomainChange = (event) => {
    const value = event.target.value;
    const { option } = event.detail;
    if (option) {
      if (!value.length) {
        setState(values => ({ ...values, domain_identifier: [option.value] }));
      } else if (value.includes(option.value)) {
        const newValue = value.filter((item) => item !== option.value);
        setState(values => ({ ...values, domain_identifier: newValue }));
      } else {
        const newValue = [...value, option.value];
        setState(values => ({ ...values, domain_identifier: newValue }));
      }

      // Clear form error for domain_identifier when a selection is made
      setFormErrors(values => ({
        ...values,
        domain_identifier: false
      }));
    }

    if (domainSearchQuery) {
      setDomainSearchQuery("");
      setDomainOptions(domainIdentifier);
    }
  };

  const handleDomainSearch = (event) => {
    if (!event.detail) {
      // Handle case when search is cleared
      setDomainSearchQuery("");
      setDomainOptions(domainIdentifier);
      return;
    }

    const searchValue = event.detail.value || "";
    setDomainSearchQuery(searchValue);

    // Filter existing domains if any
    const filtered = domainIdentifier.filter((item) => {
      const itemText = item.text.toLowerCase();
      const searchText = searchValue.toLowerCase();
      if (itemText.includes(searchText)) {
        item.query = searchValue;
        return true;
      }
      return false;
    });

    setDomainOptions(filtered);
  };

  const handleAddDomain = event => {
    event?.preventDefault?.();

    if (!domainSearchQuery.trim()) return;

    const currentValue = state.domain_identifier || [];
    const newValue = Array.isArray(currentValue) ? [...currentValue, domainSearchQuery.trim()] : [domainSearchQuery.trim()];
    const newDomainData = [...domainIdentifier, { text: domainSearchQuery.trim(), value: domainSearchQuery.trim() }];

    setDomainIdentifier(newDomainData);
    setDomainOptions(newDomainData);
    setState(values => ({ ...values, domain_identifier: newValue }));
    setDomainSearchQuery("");

    // Validate the new domains after adding
    const validDomains = verifyDomainIdentifiers(newValue);
    if (validDomains) {
      setValidationErrors({
        domain_identifier: true
      });
    } else {
      setValidationErrors({
        domain_identifier: false
      });
    }

    // Clear form error for domain_identifier when a domain is added
    setFormErrors(values => ({
      ...values,
      domain_identifier: false
    }));
  };

  const getCurrentDomainValues = () => {
    if (!state.domain_identifier) return [];
    if (Array.isArray(state.domain_identifier)) return state.domain_identifier;
    if (typeof state.domain_identifier === 'string') {
      return state.domain_identifier.split(',').filter(d => d.trim());
    }
    return [];
  };

  const formItemChange = e => {
    // Handle Harbor select (e.detail), Harbor input (e.target) events
    let name, value;

    if (e.detail) {
      // Harbor select component
      name = e.target.name;
      value = e.detail.value;
    } else {
      // Harbor input components
      const target = e.target || e.currentTarget;
      name = target.name;
      value = target.value;
    }

    if (name === "domain_identifier") {
      const validDomains = verifyDomainIdentifiers(value);
      if (validDomains) {
        setValidationErrors({
          [name]: true
        });
      } else {
        setValidationErrors({
          [name]: false
        });
      }
    }
    setState(values => ({ ...values, [name]: value }));
    setFormErrors(values => ({
      ...values,
      [name]: value && value.trim() !== "" ? false : true
    }));
  };
  return (
    <>
      {error ? (
        <Suspense fallback={<Spinner />}>
          <ErrorComponent className={"large-dashlet-error"} {...error} />
        </Suspense>
      ) : (
        <form className={css["oidc-form"]} onSubmit={onSave}>
          {showSpinner && <Spinner />}
          <HbrInput
            maxwidth="100%"
            size="medium"
            name="client_id"
            label={tViews('oidcClientId')}
            placeholder={tViews('oidcClientId')}
            value={state.client_id}
            onHbr-input={formItemChange}
            required={true}
            disabled={idpAction === IDP_ACTIONS.VIEW || idpAction === IDP_ACTIONS.EDIT}
            error={!!formErrors.client_id}
          >
            {formErrors.client_id && (
              <div slot="help-text" className="error-text">
                {tViews('idpConfigFieldRequired')}
              </div>
            )}
          </HbrInput>
          {idpAction === IDP_ACTIONS.DEFINE && (
            <HbrInput
              type="password"
              maxwidth="100%"
              size="medium"
              name="client_secret"
              label={tViews('oidcClientSecret')}
              placeholder={tViews('oidcClientSecret')}
              value={state.client_secret}
              onHbr-input={formItemChange}
              required={idpAction === IDP_ACTIONS.DEFINE ? true : false}
              disabled={idpAction === IDP_ACTIONS.VIEW || idpAction === IDP_ACTIONS.EDIT}
              error={!!formErrors.client_secret}
            >
              {formErrors.client_secret && (
                <div slot="help-text" className="error-text">
                  {tViews('idpConfigFieldRequired')}
                </div>
              )}
            </HbrInput>
          )}
          {idpAction !== IDP_ACTIONS.DEFINE && (
            <HbrInput
              maxwidth="100%"
              size="medium"
              name="redirect_uri"
              label={tViews('oidcRedirectUri')}
              placeholder={tViews('oidcRedirectUri')}
              value={state.redirect_uri}
              disabled={true}
            ></HbrInput>
          )}
          <HbrInput
            maxwidth="100%"
            size="medium"
            name="issuer"
            label={tViews('oidcIssuer')}
            placeholder={tViews('oidcIssuer')}
            value={state.issuer}
            onHbr-input={formItemChange}
            required={true}
            disabled={idpAction === IDP_ACTIONS.VIEW || idpAction === IDP_ACTIONS.EDIT}
            error={!!formErrors.issuer}
          >
            {formErrors.issuer && (
              <div slot="help-text" className="error-text">
                {tViews('idpConfigFieldRequired')}
              </div>
            )}
          </HbrInput>
          <HbrInput
            maxwidth="100%"
            size="medium"
            name="auth_endpoint"
            label={tViews('oidcAuthorizationEndpoint')}
            placeholder={tViews('oidcAuthorizationEndpoint')}
            value={state.auth_endpoint}
            onHbr-input={formItemChange}
            required={true}
            disabled={idpAction === IDP_ACTIONS.VIEW || idpAction === IDP_ACTIONS.EDIT}
            error={!!formErrors.auth_endpoint}
          >
            {formErrors.auth_endpoint && (
              <div slot="help-text" className="error-text">
                {tViews('idpConfigFieldRequired')}
              </div>
            )}
          </HbrInput>
          <HbrInput
            maxwidth="100%"
            size="medium"
            name="token_endpoint"
            label={tViews('oidcTokenEndpoint')}
            placeholder={tViews('oidcTokenEndpoint')}
            value={state.token_endpoint}
            onHbr-input={formItemChange}
            required={true}
            disabled={idpAction === IDP_ACTIONS.VIEW || idpAction === IDP_ACTIONS.EDIT}
            error={!!formErrors.token_endpoint}
          >
            {formErrors.token_endpoint && (
              <div slot="help-text" className="error-text">
                {tViews('idpConfigFieldRequired')}
              </div>
            )}
          </HbrInput>
          <HbrInput
            maxwidth="100%"
            size="medium"
            name="jwks_endpoint"
            label={tViews('oidcJwksEndpoint')}
            placeholder={tViews('oidcJwksEndpoint')}
            value={state.jwks_endpoint}
            onHbr-input={formItemChange}
            required={true}
            disabled={idpAction === IDP_ACTIONS.VIEW || idpAction === IDP_ACTIONS.EDIT}
            error={!!formErrors.jwks_endpoint}
          >
            {formErrors.jwks_endpoint && (
              <div slot="help-text" className="error-text">
                {tViews('idpConfigFieldRequired')}
              </div>
            )}
          </HbrInput>
          <HbrInput
            maxwidth="100%"
            size="medium"
            name="userinfo_endpoint"
            label={tViews('oidcUserinfoEndpoint')}
            placeholder={tViews('oidcUserinfoEndpoint')}
            value={state.userinfo_endpoint}
            required={true}
            disabled={idpAction === IDP_ACTIONS.VIEW || idpAction === IDP_ACTIONS.EDIT}
            onHbr-input={formItemChange}
            error={!!formErrors.userinfo_endpoint}
          >
            {formErrors.userinfo_endpoint && (
              <div slot="help-text" className="error-text">
                {tViews('idpConfigFieldRequired')}
              </div>
            )}
          </HbrInput>
          <HbrInput
            maxwidth="100%"
            size="medium"
            name="default_user_role"
            label={tViews('samlDefaultUserRole')}
            placeholder={tViews('samlDefaultUserRole')}
            value={state.default_user_role}
            onHbr-input={formItemChange}
            required={true}
            disabled={idpAction === IDP_ACTIONS.VIEW}
            error={!!formErrors.default_user_role}
          >
            {formErrors.default_user_role && (
              <div slot="help-text" className="error-text">
                {tViews('idpConfigFieldRequired')}
              </div>
            )}
          </HbrInput>
          <HbrSelect
            maxwidth="100%"
            name="domain_identifier"
            placeholder={idpAction === IDP_ACTIONS.DEFINE ? tViews('oidcAddDomainIdentifier') : tViews('oidcDomainIdentifier')}
            onHbr-change={handleDomainChange}
            label={tViews('oidcDomainIdentifier')}
            searchable={true}
            onHbr-search={handleDomainSearch}
            onHbr-clear={() => {
              setState(values => ({ ...values, domain_identifier: [] }));
              setDomainSearchQuery("");
              setDomainOptions(domainIdentifier);
            }}
            clearable={true}
            variant="tag"
            disabled={idpAction === IDP_ACTIONS.VIEW}
            required
            value={getCurrentDomainValues()}
            error={!!(formErrors.domain_identifier || validationErrors.domain_identifier)}
          >
            {(formErrors.domain_identifier || validationErrors.domain_identifier) && (
              <div slot="help-text" className="error-text">
                {formErrors.domain_identifier
                  ? tViews('idpConfigFieldRequired')
                  : validationErrors.domain_identifier
                  ? tViews('idpConfigInvalidDomains')
                  : ""}
              </div>
            )}
            {domainOptions.length ? (
              domainOptions.map((option, idx) => (
                <HbrOption value={option.value} key={idx.toString()} allowSpace={true}>
                  {domainSearchQuery ? (
                    <HbrHighlight
                      text={option.text}
                      queryText={option.query}
                      continuous={false}
                    />
                  ) : (
                    option.text
                  )}
                </HbrOption>
              ))
            ) : (
              <>
                <div className="hbr-css__layout-row hbr-css__layout-justify-center no-matches-section">
                  <HbrIllustration size="small">
                    <div slot="body" className="hbr-css__text-primary-xs">
                      No matches found.
                    </div>
                  </HbrIllustration>
                </div>
                {domainSearchQuery && (
                  <div className="add-domain-section">
                    <span>&quot;{domainSearchQuery}&quot;</span>
                    <HbrButton
                      variant="primary"
                      size="small"
                      type="button"
                      onClick={handleAddDomain}
                    >
                      + Add
                    </HbrButton>
                  </div>
                )}

              </>
            )}
          </HbrSelect>
          <div className="cta-bar">
            {idpAction !== IDP_ACTIONS.VIEW && (
              <>
                <HbrButton
                  name="formUpdate"
                  variant="link"
                  type="button"
                  onClick={onClose}
                >{tViews('idpConfigCancel')}</HbrButton>
                <HbrButton
                  name="formSubmit"
                  type="submit"
                  disabled={idpAction === IDP_ACTIONS.EDIT && !stateChanged}
                >{tViews('idpConfigSave')}</HbrButton>
              </>
            )}
          </div>
        </form>
      )}
    </>
  );
};

Oidc.propTypes = {
  actions: PropTypes.object.isRequired,
  accounts: PropTypes.object.isRequired
};

export default (
  connect(mapStateToProps, mapDispatchToProps)(AppHoc(Oidc))
);
